import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash, PencilSimple, UploadSimple, SealCheck } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

const EMPTY = {
  title: "", room_type: "1BHK", city: "", locality: "", address: "", description: "",
  monthly_rent: 20000, security_deposit: 40000, lock_in_months: 6, max_lease_months: 12,
  furnishing: "Semi-Furnished", amenities: "", images: "", available_from: "",
  preferred_tenant: "Any", electricity_bill_url: null,
};

const TYPES = ["Single Room", "1BHK", "2BHK", "3BHK", "Studio", "PG"];
const FURN = ["Unfurnished", "Semi-Furnished", "Fully Furnished"];
const TENANT = ["Any", "Family", "Bachelor", "Female Only", "Male Only"];

function ImageUploader({ label, url, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success(`${label} uploaded`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">{label}</Label>
      {url ? (
        <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
          <SealCheck size={16} weight="fill" className="text-emerald-600" />
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline flex-1 truncate">{url}</a>
          <button onClick={() => onChange(null)} className="text-xs text-red-700">Remove</button>
        </div>
      ) : (
        <>
          <input type="file" accept="image/*,application/pdf" onChange={handle} className="hidden" id={testId} data-testid={testId} disabled={uploading} />
          <label htmlFor={testId} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-black/20 cursor-pointer hover:border-gold text-sm">
            <UploadSimple size={16} /> {uploading ? "Uploading..." : "Upload"}
          </label>
        </>
      )}
    </div>
  );
}

export default function PartnerRoomsPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [imageUploads, setImageUploads] = useState([]);

  const refresh = () => {
    api.get("/partner/properties").then(({ data }) => setProperties(data));
    api.get("/rentals/owner").then(({ data }) => setRentals(data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setEditingId(null); setForm(EMPTY); setImageUploads([]); setOpen(true);
  };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      ...p,
      amenities: (p.amenities || []).join(", "),
      images: (p.images || []).join(", "),
    });
    setImageUploads(p.images || []);
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUploads((prev) => [...prev, data.url]);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
  };

  const removeImage = (url) => setImageUploads((prev) => prev.filter((u) => u !== url));

  const save = async () => {
    const payload = {
      ...form,
      monthly_rent: Number(form.monthly_rent),
      security_deposit: Number(form.security_deposit),
      lock_in_months: Number(form.lock_in_months),
      max_lease_months: Number(form.max_lease_months),
      amenities: String(form.amenities).split(",").map((s) => s.trim()).filter(Boolean),
      images: imageUploads,
    };
    try {
      if (editingId) await api.put(`/properties/${editingId}`, payload);
      else await api.post("/properties", payload);
      toast.success(editingId ? "Updated" : "Listing created");
      setOpen(false); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try { await api.delete(`/properties/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Room Owner</p>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <h1 className="font-serif text-4xl lg:text-5xl text-midnight">Your Rental Listings</h1>
        <a href="/partner-agreement?type=room" target="_blank" rel="noreferrer"
           data-testid="view-room-agreement-link"
           className="text-xs px-4 py-2 rounded-full bg-stone hover:bg-gold/10 text-midnight border border-black/10">
          View Partner Agreement
        </a>
      </div>
      <p className="text-sm text-neutral-500 mb-8">
        Manage your properties, view tenant applications, and track payouts.{" "}
        <Link to="/payouts" className="text-gold underline">Go to Payouts →</Link>
      </p>

      {!user?.is_verified && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          You need to complete <Link to="/verification" className="underline font-medium">ID verification</Link> before listing a property.
        </div>
      )}

      <Tabs defaultValue="properties">
        <TabsList className="mb-8">
          <TabsTrigger value="properties" data-testid="tab-properties">Properties</TabsTrigger>
          <TabsTrigger value="rentals" data-testid="tab-rentals">Rental Applications</TabsTrigger>
        </TabsList>

        <TabsContent value="properties">
          <div className="flex justify-between items-center mb-6">
            <p className="text-sm text-neutral-500">{properties.length} propert{properties.length === 1 ? "y" : "ies"}</p>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} disabled={!user?.is_verified && user?.role !== "admin"} className="bg-midnight hover:bg-midnight-hover text-bone" data-testid="new-property-btn">
                  <Plus size={16} className="mr-2" /> List a Room
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId ? "Edit" : "Add"} Property</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Sunlit 1BHK in Bandra..." data-testid="p-title" /></div>
                  <div><Label>Room Type</Label>
                    <Select value={form.room_type} onValueChange={(v) => setForm({ ...form, room_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Furnishing</Label>
                    <Select value={form.furnishing} onValueChange={(v) => setForm({ ...form, furnishing: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{FURN.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="p-city" /></div>
                  <div><Label>Locality</Label><Input value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Full address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div><Label>Monthly Rent (₹)</Label><Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} data-testid="p-rent" /></div>
                  <div><Label>Security Deposit (₹)</Label><Input type="number" value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: e.target.value })} /></div>
                  <div><Label>Lock-in (months)</Label><Input type="number" value={form.lock_in_months} onChange={(e) => setForm({ ...form, lock_in_months: e.target.value })} /></div>
                  <div><Label>Max lease (months)</Label><Input type="number" value={form.max_lease_months} onChange={(e) => setForm({ ...form, max_lease_months: e.target.value })} /></div>
                  <div><Label>Available from</Label><Input type="date" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} /></div>
                  <div><Label>Preferred Tenant</Label>
                    <Select value={form.preferred_tenant} onValueChange={(v) => setForm({ ...form, preferred_tenant: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TENANT.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Amenities (comma-separated)</Label><Input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} placeholder="WiFi, AC, Parking, Lift" /></div>

                  <div className="col-span-2">
                    <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">Property Photos</Label>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {imageUploads.map((url) => (
                        <div key={url} className="relative aspect-square rounded-lg overflow-hidden bg-stone">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          <button onClick={() => removeImage(url)} className="absolute top-1 right-1 bg-red-700 text-white w-6 h-6 rounded-full text-xs flex items-center justify-center">×</button>
                        </div>
                      ))}
                    </div>
                    <input type="file" accept="image/*" onChange={uploadImage} className="hidden" id="p-image" data-testid="p-image" />
                    <label htmlFor="p-image" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-black/20 cursor-pointer text-sm hover:border-gold">
                      <UploadSimple size={16} /> Add photo
                    </label>
                  </div>

                  <div className="col-span-2">
                    <ImageUploader label="Electricity Bill (for verification)" url={form.electricity_bill_url}
                      onChange={(url) => setForm({ ...form, electricity_bill_url: url })} testId="p-electricity-bill" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={save} className="bg-midnight hover:bg-midnight-hover text-bone" data-testid="save-property-btn">{editingId ? "Update" : "Publish Listing"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Title</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Type</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">City</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Rent</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {properties.length === 0 ? (
                  <tr><td colSpan={5} className="p-12 text-center text-neutral-500">No properties yet. Click <strong>List a Room</strong> to add your first.</td></tr>
                ) : properties.map((p) => (
                  <tr key={p.id} className="border-t border-black/5">
                    <td className="p-4 font-medium text-midnight">{p.title}</td>
                    <td className="p-4">{p.room_type}</td>
                    <td className="p-4 text-neutral-600">{p.city}</td>
                    <td className="p-4">₹{p.monthly_rent.toLocaleString("en-IN")}</td>
                    <td className="p-4 flex gap-2 justify-end">
                      <button onClick={() => openEdit(p)} className="p-2 hover:bg-stone rounded-lg"><PencilSimple size={16} /></button>
                      <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-700"><Trash size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="rentals">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Tenant</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Property</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Move-in</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Lease</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Payout</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {rentals.length === 0 ? (
                  <tr><td colSpan={6} className="p-12 text-center text-neutral-500">No rental applications yet</td></tr>
                ) : rentals.map((r) => (
                  <tr key={r.id} className="border-t border-black/5">
                    <td className="p-4"><p className="text-midnight font-medium">{r.user_name}</p><p className="text-xs text-neutral-500">{r.user_email}</p></td>
                    <td className="p-4">{r.property_title}</td>
                    <td className="p-4 text-xs">{new Date(r.start_date).toLocaleDateString("en-IN")}</td>
                    <td className="p-4">{r.lease_months} mo</td>
                    <td className="p-4 text-emerald-700 font-medium">₹{(r.payout_to_owner || 0).toLocaleString("en-IN")}</td>
                    <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-stone">{r.status.replace("_", " ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
